import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import {
  AccountStatus,
  Box,
  ErrorModal,
  FormProvider,
  Inscription,
  Preloader,
  Text,
  WarningCard,
  WarningIcon,
  WarnModal,
} from '@common-fe/common-fe';
import { toNumber } from 'lodash';
import styled from 'styled-components';

import { ROUTES } from '@/common';
import { REQUIRED_NOT_EMPTY } from '@/common/constants';
import { ModalWrapper } from '@/components';
import AppButton from '@/components/controls/AppButton';
import { useGetOrganizationById } from '@/modules/core';
import Immediately from '@/modules/ReimburseMe/ConfirmationStep/Immediately';
import { useOrganizationProperties } from '@/modules/ReimburseMe/hooks';
import useAddPaymentMethodQuery, {
  getEmployeePaymentMethods,
} from '@/modules/ReimburseMe/queries/useAddPaymentMethod.query';
import { useGetPaymentMethods } from '@/modules/ReimburseMe/queries/useGetPaymentMethods.query';
import {
  PaymentMethod,
  PaymentMethodOwnerType,
  PaymentMethodType,
  PaymentMethodVerificationStatus,
} from '@/modules/ReimburseMe/types/payment.types';

import { usePaymentMethodHasPendingMessage } from '../../TransactionDetails/components/PaymentMethodLabel/usePaymentMethodHasPendingMessage';
import { MyDocumentsTabs } from '../Documents/myDocuments.types';
import useMyDocumentsTabStore from '../Documents/stores/useMyDocumentsTab.store';
import { HSA_TYPE_KEY, useGetEnrollments } from '../hooks/useGetEnrollment';
import { usePaymentMethodsStore } from '../PersonalInformation/PaymentMethodInfo/usePaymentMethods.store';
import { useGetEmployeeByIdExtended } from '../queries';
import useTaxFormHighlightStore from '../TaxForms/stores/useTaxFormHighlight.store';

import { ExcessContributionForm } from './ExcessContributionForm';
import { UploadProofFile } from './UploadProofFile';
import {
  ExcessContributionFormData,
  ExcessContributionFormField,
  useExcessContributionForm,
} from './useExcessContributionForm';
import { useCreateExcessContributionQuery } from './useExcessContributionWithdraw.query';
import useGetExcessContributionQuery from './useGetExcessContribution.query';
import { UploadExcessContributionFile } from './useUploadExcessContributionFile.query';

const FormWrapper = styled(Box)`
  input {
    width: 335px;
  }
`;
const StyledWarningCard = styled(WarningCard)`
  border-color: transparent;
`;

const getEmptyError = (amount?: string, otherAmount?: string) => {
  if (!amount) return REQUIRED_NOT_EMPTY;
  if (toNumber(amount) + toNumber(otherAmount || 0) <= 0) return 'Total must be greater than 0';
  return '';
};
const getErrors = (
  formValues: ExcessContributionFormData,
  paymentEmployeeFee?: number,
  availableHsaAccountBalance?: number
) => {
  const excessAmount = formValues[ExcessContributionFormField.excessAmount];
  const excessEarningsAmount = formValues[ExcessContributionFormField.excessEarningsAmount];
  const total =
    toNumber(excessAmount || 0) + toNumber(excessEarningsAmount || 0) + toNumber(paymentEmployeeFee || 0);
  const hasBalanceOverrunError =
    typeof availableHsaAccountBalance === 'number' && total > availableHsaAccountBalance;
  const balanceOverrunError = hasBalanceOverrunError
    ? 'The total withdraw must be less than your current HSA balance'
    : '';
  return {
    [ExcessContributionFormField.excessAmount]:
      getEmptyError(excessAmount, excessEarningsAmount) || balanceOverrunError,
    [ExcessContributionFormField.excessEarningsAmount]:
      getEmptyError(excessEarningsAmount, excessAmount) || balanceOverrunError,
  };
};

interface Props {
  onSetVisible: (visible: boolean) => void;
  isEditMode?: boolean;
  id?: string;
  accountId?: string;
}

const ExcessContributionWithdrawModal: React.FC<Props> = ({ onSetVisible, isEditMode, id, accountId }) => {
  const [isPaymentMethodCreated, setIsPaymentMethodCreated] = useState(false);
  const [isPaymentMethodCreating, setIsPaymentMethodCreating] = useState(false);
  const {
    params: { id: employeeId },
  } = useRouteMatch<{ id: string }>();
  const { save, isLoading: isCreatingPaymentMethod } = useAddPaymentMethodQuery(employeeId, () => {
    setIsPaymentMethodCreating(false);
    setIsPaymentMethodCreated(true);
  });
  const [fileUrl, setFileUrl] = useState<UploadExcessContributionFile | undefined>();
  const { data: employee } = useGetEmployeeByIdExtended(employeeId);
  const { formattedData } = useGetOrganizationById(employee?.organizationId);
  const [isSetupPaymentMethod, setIsSetupPaymentMethod] = useState(!isEditMode);
  const { excessContribution, isLoading: isExcessContributionLoading } = useGetExcessContributionQuery(
    id,
    !isEditMode
  );
  const history = useHistory();
  const location = useLocation();
  const setHighlightedTaxFormId = useTaxFormHighlightStore((state) => state.setHighlightedTaxFormId);
  const setPendingTab = useMyDocumentsTabStore((state) => state.setPendingTab);

  const [createdExcessContributionId, setCreatedExcessContributionId] = useState<number | null>(null);
  const [hasSuccessModal, setHasSuccessModal] = useState(false);

  const handleShowRequest = useCallback(() => {
    if (createdExcessContributionId) {
      setHighlightedTaxFormId(String(createdExcessContributionId));
    }

    const employeeViewPath = ROUTES.EMPLOYEE_VIEW(employeeId);

    if (location.pathname === employeeViewPath) {
      setPendingTab(MyDocumentsTabs.TAX_FORMS);
      document.getElementById('documents')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      setPendingTab(MyDocumentsTabs.TAX_FORMS);
      history.push(`${employeeViewPath}?scrollToModule=documents`);
    }

    setHasSuccessModal(false);
    onSetVisible(false);
  }, [
    createdExcessContributionId,
    employeeId,
    location.pathname,
    setHighlightedTaxFormId,
    setPendingTab,
    history,
    onSetVisible,
  ]);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { checkIsAvailablePaymentMethod } = useOrganizationProperties(isEditMode);
  const { data, isLoading: isHsaAccountLoading } = useGetEnrollments({ enabled: isEditMode, employeeId });
  const hsaAccount = useMemo(() => {
    if (accountId) {
      return data.find(
        (item) =>
          item.id === `${accountId}` &&
          item.accountType === HSA_TYPE_KEY &&
          item.accountStatus === AccountStatus.OPEN
      );
    }
    return data.find(
      (item) => item.accountType === HSA_TYPE_KEY && item.accountStatus === AccountStatus.OPEN
    );
  }, [data, accountId]);
  const availableHsaAccountBalance = useMemo(() => hsaAccount?.available, [hsaAccount]);
  const handleSetPaymentMethods = usePaymentMethodsStore((state) => state.handleSetPaymentMethods);
  const selectedMethod = usePaymentMethodsStore((state) => state.selectedMethod);

  const activeMethod = usePaymentMethodsStore((state) => state.activeMethod);

  const hasPendingStatus = useMemo(
    () =>
      activeMethod?.verificationStatus === PaymentMethodVerificationStatus.PENDING ||
      activeMethod?.verificationStatus === PaymentMethodVerificationStatus.PENDING_MANUAL,
    [activeMethod]
  );
  const handleSetActivePaymentMethod = usePaymentMethodsStore((state) => state.handleSetActivePaymentMethod);
  const handleSetActivePaymentMethodById = usePaymentMethodsStore(
    (state) => state.handleSetActivePaymentMethodById
  );
  useEffect(() => {
    if (isEditMode || !excessContribution?.paymentMethodId || !activeMethod) return;
    handleSetActivePaymentMethodById(excessContribution.paymentMethodId);
    setIsSetupPaymentMethod(false);
  }, [handleSetActivePaymentMethodById, isEditMode, excessContribution, activeMethod]);
  const paymentEmployeeFee = useMemo(() => {
    const fee = formattedData?.overridableProperties?.allowedPaymentMethodConfigs?.find(
      (item) => item?.paymentMethodType === selectedMethod?.type
    )?.paymentEmployeeFee;
    if (fee) return toNumber(fee);
  }, [selectedMethod, formattedData]);
  const handleGettingPaymentMethods = useCallback(
    (paymentMethods: PaymentMethod[]) => {
      if (
        (!paymentMethods.length || !getEmployeePaymentMethods(paymentMethods)?.length) &&
        !isPaymentMethodCreating &&
        !isCreatingPaymentMethod
      ) {
        setIsPaymentMethodCreating(true);
        save({
          paymentType: PaymentMethodType.CHECK,
          paymentOwnerType: PaymentMethodOwnerType.EMPLOYEE,
        });
      }
      handleSetPaymentMethods(
        paymentMethods.filter((method) => method?.paymentMethodOwner?.employeeId === employeeId)
      );
    },
    [employeeId, handleSetPaymentMethods, save, isCreatingPaymentMethod, isPaymentMethodCreating]
  );
  const { isLoading, refetch, isRefetching } = useGetPaymentMethods(employeeId, handleGettingPaymentMethods);
  useEffect(() => {
    if (isPaymentMethodCreated) {
      refetch();
    }
  }, [isPaymentMethodCreated, refetch]);
  const selectedPaymentMethod = usePaymentMethodsStore((state) => state.selectedMethod);
  const isPaymentMethodAvailable = checkIsAvailablePaymentMethod(selectedPaymentMethod);

  const methods = useExcessContributionForm();
  const formValues = methods.watch();
  const errors = useMemo(
    () => getErrors(formValues, paymentEmployeeFee, availableHsaAccountBalance),
    [formValues, paymentEmployeeFee, availableHsaAccountBalance]
  );

  useEffect(() => {
    if (isEditMode) return;
    if (!excessContribution?.amount && !excessContribution?.excessEarnings) return;
    if (excessContribution?.amount) {
      methods.setValue(ExcessContributionFormField.excessAmount, excessContribution.amount);
    }
    if (excessContribution?.excessEarnings) {
      methods.setValue(ExcessContributionFormField.excessEarningsAmount, excessContribution.excessEarnings);
    }
  }, [excessContribution, isEditMode, methods]);
  useEffect(() => {
    if (!isSubmitted) return;

    if (
      methods.formState.errors[ExcessContributionFormField.excessAmount]?.message !==
      errors[ExcessContributionFormField.excessAmount]
    ) {
      methods.setError(ExcessContributionFormField.excessAmount, {
        message: errors[ExcessContributionFormField.excessAmount],
      });
    }
    if (
      methods.formState.errors[ExcessContributionFormField.excessEarningsAmount]?.message !==
      errors[ExcessContributionFormField.excessEarningsAmount]
    ) {
      methods.setError(ExcessContributionFormField.excessEarningsAmount, {
        message: errors[ExcessContributionFormField.excessEarningsAmount],
      });
    }
  }, [paymentEmployeeFee, availableHsaAccountBalance, formValues, methods, isSubmitted, errors]);

  useEffect(() => {
    if (isPaymentMethodAvailable) handleSetActivePaymentMethod();
  }, [handleSetActivePaymentMethod, isPaymentMethodAvailable]);

  const { create, isLoading: isCreating } = useCreateExcessContributionQuery(
    (data) => {
      if (data?.excess_contribution_id) {
        setCreatedExcessContributionId(data.excess_contribution_id);
      }
      setHasSuccessModal(true);
    },
    (error?: string) => setErrorMessage(error || '')
  );
  const handleSubmit = useCallback(() => {
    setIsSubmitted(true);
    if (
      !fileUrl ||
      errors[ExcessContributionFormField.excessAmount] ||
      errors[ExcessContributionFormField.excessEarningsAmount] ||
      !selectedMethod?.id
    ) {
      return;
    }

    create({
      employee_id: toNumber(employeeId),
      account_id: toNumber(hsaAccount?.id),
      amount: toNumber(formValues[ExcessContributionFormField.excessAmount]),
      excess_earnings: toNumber(formValues[ExcessContributionFormField.excessEarningsAmount]),
      payment_method_id: toNumber(selectedMethod?.id),
      file_url: fileUrl?.s3key || '',
    });
  }, [setIsSubmitted, formValues, create, employeeId, selectedMethod, hsaAccount, errors, fileUrl]);

  const { hasAccountVerificationPendingMessage } = usePaymentMethodHasPendingMessage(
    selectedMethod,
    isEditMode
  );

  return (
    <>
      <WarnModal
        testId="success"
        isSuccessIcon
        header="Excess contribution request was successfully submitted!"
        submitButtonNode={
          <AppButton
            testId="gotIt"
            width="200px"
            onClick={() => {
              setHasSuccessModal(false);
              onSetVisible(false);
            }}
          >
            Got It!
          </AppButton>
        }
        cancelButtonNode={
          <AppButton testId="showRequest" buttonType="secondary" width="200px" onClick={handleShowRequest}>
            Show Request
          </AppButton>
        }
        visible={hasSuccessModal}
        onSetVisible={() => {
          setHasSuccessModal(false);
          onSetVisible(false);
        }}
      />
      <ErrorModal
        testId="error"
        header="Error"
        helpText={errorMessage}
        visible={Boolean(errorMessage)}
        onSetVisible={() => {
          setErrorMessage('');
          onSetVisible(false);
        }}
        buttonText="Close"
      />
      <ModalWrapper
        isClosingByClickOutsideBlocked
        visible={!hasSuccessModal && !errorMessage}
        onSetVisible={onSetVisible}
        title="Request Excess Contribution Withdraw"
        contentStyle={{
          maxWidth: '720px',
          width: '100%',
        }}
      >
        <Text size="medium" color="textBody" textAlign="center" margin={{ bottom: 'spacing24' }}>
          Excess contributions are amounts you put into your HSA beyond the IRS annual limit, which must be
          corrected to avoid taxes and penalties.
        </Text>

        <Box direction="column" gap="spacing12" background="module" pad="spacing24" round="moduleRound">
          <Box round="container1Round" border={{ size: 'small', color: 'border2' }}>
            <FormWrapper background="canvas" round="container1Round">
              {isExcessContributionLoading ? (
                <Box pad="spacing24">
                  <Preloader testId="FormLoading" />
                </Box>
              ) : (
                <FormProvider {...methods}>
                  <ExcessContributionForm isEditMode={isEditMode} />
                </FormProvider>
              )}
            </FormWrapper>
          </Box>

          <Box
            data-testId="refundMethodSection"
            direction="column"
            round="container1Round"
            border={{ size: 'small', color: 'border2' }}
            background="canvas"
          >
            <Immediately
              isRefund
              isLoading={
                isLoading ||
                isExcessContributionLoading ||
                isSetupPaymentMethod ||
                isPaymentMethodCreating ||
                isCreatingPaymentMethod ||
                isRefetching
              }
              allowedPaymentMethodConfigs={formattedData?.overridableProperties?.allowedPaymentMethodConfigs}
              employee={employee}
              paymentMethod={selectedMethod}
            />
            {isPaymentMethodCreated ? (
              <Box
                background="warningContainer"
                pad={{ vertical: 'spacing16', horizontal: 'medium' }}
                margin={{ bottom: 'medium', top: '-8px', horizontal: 'medium' }}
                round="container2Round"
                border={{ color: 'warningBorder', size: 'small' }}
              >
                <Box direction="row" align="center">
                  <WarningIcon />
                  <Inscription lineHeight="20px" weight="bold" margin={{ left: 'spacing8' }}>
                    The refund method is set to Check because the employee has not added another option.
                  </Inscription>
                </Box>
              </Box>
            ) : null}
            {hasAccountVerificationPendingMessage ? (
              <Box margin={{ top: '-8px' }} pad={{ horizontal: 'spacing24', bottom: 'spacing24' }}>
                <StyledWarningCard
                  testId="paymentMethodPendingVerification"
                  containerProps={{
                    pad: { vertical: 'spacing16', horizontal: 'spacing24' },
                  }}
                >
                  <Box direction="row" align="center">
                    <WarningIcon />
                    <Inscription
                      color="textBody"
                      weight="bold"
                      lineHeight="20px"
                      margin={{ left: 'spacing8' }}
                    >
                      You cannot use this payment method yet
                    </Inscription>
                  </Box>
                  <Inscription color="textBody" lineHeight="20px" margin={{ top: 'spacing8' }}>
                    This payment method is still pending verification. You'll be able to use it as soon as
                    your bank account is confirmed.
                  </Inscription>
                </StyledWarningCard>
              </Box>
            ) : null}
          </Box>

          {isEditMode || (!isEditMode && excessContribution?.fileUrl) ? (
            <Box
              data-testId="refundMethodSection"
              direction="column"
              pad={{ vertical: 'spacing16', horizontal: 'spacing24' }}
              round="container1Round"
              border={{ size: 'small', color: 'border2' }}
              background="canvas"
            >
              <UploadProofFile
                isEditMode={isEditMode}
                fileUrl={isEditMode ? fileUrl : { file_url: excessContribution?.fileUrl }}
                setFileUrl={setFileUrl}
                employeeId={employeeId}
              />
            </Box>
          ) : null}
        </Box>

        <Box margin={{ top: 'spacing24' }}>
          {isSubmitted && !fileUrl ? (
            <Box align="end" margin={{ bottom: 'spacing12' }}>
              <Text color="danger">
                Please upload a file as proof of the participant’s withdrawal request.
              </Text>
            </Box>
          ) : null}
          {isSubmitted && !selectedMethod?.id ? (
            <Box align="end" margin={{ bottom: 'spacing12' }}>
              <Text color="danger">Select a payment method</Text>
            </Box>
          ) : null}
          <Box data-testId="actionButtons" direction="row" gap="spacing12" justify="end">
            <AppButton
              testId="cancel"
              buttonType="secondary"
              width="160px"
              onClick={() => onSetVisible(false)}
            >
              Cancel
            </AppButton>
            {isEditMode ? (
              <AppButton
                testId="submit"
                buttonType="primary"
                width="160px"
                disabled={
                  isLoading ||
                  isHsaAccountLoading ||
                  isCreating ||
                  hasPendingStatus ||
                  hasAccountVerificationPendingMessage ||
                  !selectedMethod?.id
                }
                onClick={handleSubmit}
              >
                {isLoading || isHsaAccountLoading || isCreating ? <Preloader testId="submit" /> : 'Submit'}
              </AppButton>
            ) : null}
          </Box>
        </Box>
      </ModalWrapper>
    </>
  );
};

export default ExcessContributionWithdrawModal;
